import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const PAGE_SIZE = 25;

export default function Audit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const params = new URLSearchParams();
    params.set("skip", skip);
    params.set("limit", PAGE_SIZE);
    if (actionFilter) params.set("action", actionFilter);
    if (search) params.set("q", search);
    try {
      const r = await api(`/audit-log?${params.toString()}`);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setAvailableActions(r.available_actions || []);
    } catch (e) {
      setErr(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [skip, actionFilter, search]);

  useEffect(() => { load(); }, [load]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSkip(0);
    setSearch(searchInput.trim());
  };

  const resetFilters = () => {
    setSkip(0);
    setActionFilter("");
    setSearch("");
    setSearchInput("");
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="content">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 30, marginBottom: 6 }}>Journal d'audit</h1>
        <p style={{ color: 'var(--ink-muted)' }}>Trace immuable de toutes les actions critiques effectuées dans votre espace (conformité Polynésie française).</p>
      </div>

      {/* Filter bar */}
      <div className="panel" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }} data-testid="audit-filters">
        <div className="field" style={{ margin: 0, flex: '1 1 180px', minWidth: 0 }}>
          <label style={{ fontSize: 12 }}>Type d'action</label>
          <select className="select" value={actionFilter} onChange={e => { setSkip(0); setActionFilter(e.target.value); }} data-testid="audit-filter-action">
            <option value="">Toutes les actions</option>
            {availableActions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <form onSubmit={onSearchSubmit} className="field" style={{ margin: 0, flex: '2 1 240px', minWidth: 0, display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12 }}>Recherche (email, nom, plan, ID…)</label>
            <input type="text" className="input" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="ex: durand@…, pro, FAC-2026-001" data-testid="audit-search-input" />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" style={{ height: 38 }} data-testid="audit-search-submit">Chercher</button>
        </form>
        {(actionFilter || search) && (
          <button onClick={resetFilters} className="btn btn-ghost btn-sm" data-testid="audit-reset-filters">Réinitialiser</button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-muted)' }}>
          {total} action{total > 1 ? 's' : ''} {(actionFilter || search) ? 'filtrée' + (total > 1 ? 's' : '') : 'au total'}
        </div>
      </div>

      {err && <div className="panel" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', marginBottom: 16 }}>{err}</div>}

      {loading ? (
        <div className="panel" style={{ textAlign: 'center', padding: 40 }}><span className="spinner" style={{ display: 'inline-block', borderColor: 'var(--border-strong)', borderTopColor: 'var(--primary)' }}></span></div>
      ) : items.length === 0 ? (
        <div className="panel" style={{ textAlign: 'center', padding: 60, color: 'var(--ink-muted)' }}>
          {(actionFilter || search) ? 'Aucune action ne correspond à ces filtres.' : 'Aucune action enregistrée pour le moment.'}
        </div>
      ) : (
        <>
          <div className="table-wrap" data-testid="audit-table">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Date</th>
                  <th style={{ width: 200 }}>Action</th>
                  <th style={{ width: 140 }}>Ressource</th>
                  <th>Détail</th>
                </tr>
              </thead>
              <tbody>
                {items.map(l => (
                  <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                    <td style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
                      {new Date(l.created_at).toLocaleString("fr-FR", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td><strong style={{ fontSize: 13.5 }}>{l.action_label}</strong></td>
                    <td style={{ fontSize: 13, color: 'var(--ink-muted)' }}>{l.resource_label}</td>
                    <td style={{ fontSize: 13 }}>
                      {l.human_summary || (l.resource_id ? <span style={{ color: 'var(--ink-faint)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>réf. {l.resource_id.substring(0, 12)}…</span> : <span style={{ color: 'var(--ink-faint)' }}>—</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 4px' }} data-testid="audit-pagination">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                data-testid="audit-prev"
              >← Précédent</button>
              <span style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
                Page <strong>{currentPage}</strong> sur {totalPages}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={skip + PAGE_SIZE >= total}
                data-testid="audit-next"
              >Suivant →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
